import { getAuth, getIdTokenResult, onIdTokenChanged, ParsedToken, signInWithCustomToken } from "firebase/auth";
import { useContext, useEffect, useRef, useState } from "react";

import { apiClient } from "@/features/api/apiClient";
import { Claims, ClaimsContext } from "@/features/auth/components/ClaimsContext";
import { useBoundStore } from "@/features/offline/stores/boundStore";
import { SentryContext, SentryContextValue } from "@/features/sentry/SentryProvider";
import { useFirebaseStore } from "@/firebase";
import { ComponentWithChildren } from "@/utils/types/ComponentsWithChildren";

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

const asClaimsRecord = (value: unknown): Record<string, number> | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const numeric = Object.entries(value).filter(([, count]) => typeof count === "number");
  return numeric.length > 0 ? Object.fromEntries(numeric) : undefined;
};

const parseClaims = (raw: ParsedToken): Claims => ({
  claims: asClaimsRecord(raw.claims),
  domainId: asString(raw.domainId),
  countryId: asString(raw.countryId),
  flags: typeof raw.flags === "number" ? raw.flags : undefined,
  pharmacyId: asString(raw.pharmacyId),
  pin: typeof raw.pin === "boolean" ? raw.pin : undefined,
});

export const ClaimsProvider = ({ children }: ComponentWithChildren) => {
  const firebase = useFirebaseStore((state) => state.firebaseInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [claims, setClaims] = useState<Claims | undefined>();

  const isExchangingRef = useRef(false);

  const oldPharmacyId = useBoundStore((state: { pharmacyId: any }) => state.pharmacyId);

  const [resetData, setZustandClaims] = useBoundStore((store: { resetData: any; setClaims: any }) => [
    store.resetData,
    store.setClaims,
  ]);

  const { setMetadata } = useContext<SentryContextValue>(SentryContext);

  useEffect(() => {
    setIsLoading(true);
    const auth = getAuth(firebase);

    const tokenIdListener = onIdTokenChanged(auth, async (user) => {
      if (user == null) {
        isExchangingRef.current = false;
        setClaims(undefined);
        setZustandClaims(undefined);
        setIsLoading(false);
        return;
      }

      try {
        const tokenResult = await getIdTokenResult(user);
        const claims = parseClaims(tokenResult.claims);

        // Token Firebase brut (login email) : aucune identité métier, ni utilisateur (domainId)
        // ni pharmacie (pharmacyId). On l'échange contre un custom token portant l'identité
        // résolue en base. Les claims granulaires ne sont ajoutés qu'après le PIN.
        // onIdTokenChanged se redéclenchera.
        if (claims.domainId == null && claims.pharmacyId == null) {
          if (isExchangingRef.current) {
            return;
          }
          isExchangingRef.current = true;
          const { token } = await apiClient.tokens.create();
          await signInWithCustomToken(auth, token);
          return;
        }

        isExchangingRef.current = false;
        setClaims(claims);
        setMetadata(user, claims);
        setZustandClaims(claims);

        if (claims.pharmacyId && claims.pharmacyId !== oldPharmacyId) {
          resetData(claims);
        }
      } catch (error) {
        isExchangingRef.current = false;
        console.error("Error resolving claims:", error);
      }

      setIsLoading(false);
    });

    return () => {
      if (tokenIdListener) {
        tokenIdListener();
      }
    };
  }, [firebase, oldPharmacyId, resetData, setClaims, setIsLoading, setMetadata, setZustandClaims]);

  return (
    <ClaimsContext.Provider
      value={{
        claims,
        isLoading,
      }}
    >
      {children}
    </ClaimsContext.Provider>
  );
};
